import {
  FeedCard,
  Giide,
  IAdminUser,
  ICategory,
  IUser,
  Step,
  Subscription,
  SubscriptionsState,
  UserAudioLibrary,
  GiideAnalytics,
  PaddleAccountDetails,
  PaddleTransactions,
  ImageEditorImage,
  PasswordModalVariants,
} from './graphql/types';
import {
  CreateGiideErrorType,
  ExportVideoState,
  Filters,
  GiideAudioLibraryFetching,
  giideListPaginationPage,
  giideListSort,
  giideTabState,
  ImageEditorState,
  ImageEditorValidation,
  PaymentState,
  PreviewDetails,
  SettingsData,
  UnlinkRecordingProps,
} from './types';
import { AiChatHistory, TranscriptItemInput } from './graphql/api';
import { ModalContent } from './hooks/useGiideContentModal';

export const sliderOnAfterChange = () => ({
  type: 'ON_AFTER_CHANGE',
});

export const sliderOnChange = (value: number) => ({
  payload: value,
  type: 'ON_CHANGE',
});

export const sliderOnBeforeChange = () => ({
  type: 'ON_BEFORE_CHANGE',
});

export const setWasPlaying = (wasPlaying: boolean) => ({
  payload: wasPlaying,
  type: 'SET_WAS_PLAYING',
});

export const highlightCard = (cardId: string | null) => ({
  type: 'HIGHLIGHT_CARD',
  payload: { cardId },
});

export const updateOneGiideSubscription = (id: string, sub?: Subscription) => ({
  type: 'UPDATE_ONE_GIIDE_SUBSCRIPTION',
  payload: { id, sub },
});

export const updateSubscription = (
  key: keyof SubscriptionsState,
  sub?: Subscription
) => ({
  type: 'UPDATE_SUBSCRIPTION',
  payload: { key, sub },
});

export const updateGraphicFormScrollPos = (scrollPos: null | number) => ({
  type: 'UPDATE_GRAPHIC_FORM_SEARCH_SCROLL_POSITION',
  payload: scrollPos,
});

export const addFeedCard = (feedCard: Partial<FeedCard>) => ({
  type: 'ADD_FEED_CARD',
  payload: feedCard,
});

export const setShowButtons = (showButtons?: string) => ({
  type: 'SET_SHOW_BUTTONS',
  payload: showButtons,
});

export const createChapterFromTranscriptionModal = () => ({
  type: 'CREATE_CHAPTER_FROM_TRANSCRIPTION_MODAL',
});

export const closeRecordingProcessingModal = () => ({
  type: 'CLOSE_RECORDING_PROCESSING_MODAL',
});

export const closeRecordingErrorModal = () => ({
  type: 'CLOSE_RECORDING_ERROR_MODAL',
});

export const setRecordingProcessingModalShowed = (showed: boolean) => ({
  type: 'SET_RECORDING_PROCESSING_MODAL_SHOWED',
  payload: showed,
});

export const setRecordingProcessingModalVisible = (visible: boolean) => ({
  type: 'SET_RECORDING_PROCESSING_MODAL_VISIBLE',
  payload: visible,
});

export const setRecordingErrorModalVisible = (visible: boolean) => ({
  type: 'SET_RECORDING_ERROR_MODAL_VISIBLE',
  payload: visible,
});

export const setNewStepModalVisible = (visible: boolean) => ({
  type: 'SET_NEW_STEP_MODAL_VISIBLE',
  payload: visible,
});

export const setPlayerDuration = (length: number) => ({
  type: 'SET_PLAYER_DURATION',
  payload: length,
});

export const adjustPlayerTime = (delta: number) => ({
  payload: delta,
  type: 'ADJUST_PLAYER_TIME',
});

export const setPlayingState = (playing: boolean) => ({
  payload: playing,
  type: 'SET_PLAYING_STATE',
});

export const setSeekFunction = (seekFunction: (time: number) => void) => ({
  payload: seekFunction,
  type: 'SET_SEEK_FUNCTION',
});

export const setPlayingProgress = (time: number) => ({
  payload: time,
  type: 'SET_PLAYING_PROGRESS',
});

export const setGiides = giides => ({
  payload: giides,
  type: 'SET_GIIDES',
});

export const setLibraryGiides = libraryGiides => ({
  payload: libraryGiides,
  type: 'SET_LIBRARY_GIIDES',
});

export const setGiideListFilter = (filters: Filters) => ({
  payload: filters,
  type: 'SET_GIIDE_LIST_FILTER',
});

export const setGiideListSortType = (sortType: giideListSort) => ({
  payload: sortType,
  type: 'SET_GIIDE_LIST_SORT_TYPE',
});

export const setGiidePaginationPage = (
  paginationPage: giideListPaginationPage
) => ({
  payload: paginationPage,
  type: 'SET_GIIDE_PAGINATION_PAGE',
});

export const setGiideListViewType = (viewType: string) => ({
  payload: viewType,
  type: 'SET_GIIDE_LIST_VIEW_TYPE',
});

export const setGiideListSearch = value => ({
  payload: value,
  type: 'SET_GIIDE_LIST_SEARCH',
});

export const setGiide = (giide: Giide | null) => ({
  payload: giide,
  type: 'SET_GIIDE',
});

export const setGiideTabState = (
  state: giideTabState,
  compareIndex?: number
) => ({
  payload: { state, compareIndex },
  type: 'SET_GIIDE_TAB_STATE',
});

export const setSteps = (steps: Step[], openLast = false) => ({
  payload: { steps, openLast },
  type: 'SET_STEPS',
});

export const reorderSteps = (stepId: string, stepNewIndex: number) => ({
  payload: { stepId, stepNewIndex },
  type: 'REORDER_STEPS',
});

export const addStep = (step: Step) => ({
  payload: step,
  type: 'ADD_STEP',
});

export const deleteGiide = (id: string) => ({
  payload: id,
  type: 'DELETE_GIIDE',
});

export const addGiide = (giide: Giide) => ({
  payload: giide,
  type: 'ADD_GIIDE',
});

export const updateGiide = (giide: Partial<Giide>, current?: boolean) => ({
  payload: { giide, current },
  type: 'UPDATE_GIIDE',
});

export const updateGiideLibrary = (
  giide: Partial<Giide>,
  current?: boolean
) => ({
  payload: { giide, current },
  type: 'UPDATE_GIIDE_LIBRARY',
});

export const setUser = (user: IUser) => ({
  payload: user,
  type: 'SET_USER',
});

export const updateUser = (user: Partial<IUser>) => ({
  payload: user,
  type: 'UPDATE_USER',
});

export const setAdminUsers = (adminUsers: IAdminUser[]) => ({
  payload: adminUsers,
  type: 'SET_ADMIN_USERS',
});

export const addAdminUser = (adminUser: IAdminUser) => ({
  payload: adminUser,
  type: 'ADD_ADMIN_USER',
});

export const setTenantCategories = (tenantCategories: ICategory[]) => ({
  payload: tenantCategories,
  type: 'SET_TENANT_CATEGORIES',
});

export const deleteStep = (id: string) => ({
  payload: id,
  type: 'DELETE_STEP',
});

export const setGiideFetching = (fetching: boolean) => ({
  payload: fetching,
  type: 'SET_GIIDE_FETCHING',
});

export const setGiideAudioLibraryFetching = (
  giideAudioLibraryFetching: GiideAudioLibraryFetching
) => ({
  payload: giideAudioLibraryFetching,
  type: 'SET_GIIDE_AUDIO_LIBRARY_FETCHING',
});

export const setGiideFetchInterval = interval => ({
  payload: interval,
  type: 'SET_GIIDE_FETCH_INTERVAL',
});

export const addCardProps = (key: keyof FeedCard, value: any) => ({
  payload: { key, value },
  type: 'ADD_CARD_PROPS',
});

export const updateCardProps = (data: Partial<FeedCard>) => ({
  payload: data,
  type: 'UPDATE_CARD_PROPS',
});

export const updateEditingCardProps = (data: Partial<FeedCard>) => ({
  payload: data,
  type: 'UPDATE_EDITING_CARD_PROPS',
});

export const setCardProps = (cardProps?: Partial<FeedCard>) => ({
  payload: cardProps,
  type: 'SET_CARD_PROPS',
});

export const setEditingCardProps = (cardProps?: Partial<FeedCard>) => ({
  payload: cardProps,
  type: 'SET_EDITING_CARD_PROPS',
});

export const removeStep = id => ({
  payload: id,
  type: 'REMOVE_STEP',
});

export const updateStep = (step?: Partial<Step>) => ({
  payload: step,
  type: 'UPDATE_STEP',
});

export const setOpenedStep = (index?: number) => ({
  payload: index,
  type: 'SET_OPENED_STEP',
});

export const setAvatar = data => ({
  type: 'SET_AVATAR',
  payload: data,
});

export const setLibraryAuthorAvatar = data => ({
  type: 'SET_LIBRARY_AVATAR',
  payload: data,
});

export const logout = () => ({
  type: 'LOGOUT',
});

export const setProcessingRecording = data => ({
  type: 'SET_PROCESSING_RECORDING',
  payload: data,
});

export const setIsScrapingUrl = (isScraping: boolean) => ({
  type: 'SET_IS_SCRAPING',
  payload: isScraping,
});

export const setIsInitialScrapingDone = (
  isInitialScrapingDone: boolean = true
) => ({
  type: 'SET_IS_INIT_SCRAPING_DONE',
  payload: isInitialScrapingDone,
});

export const moveFeedCard = (
  recordingId: string,
  cardId: string,
  startTime: number
) => ({
  payload: { recordingId, cardId, startTime },
  type: 'MOVE_FEED_CARD',
});

export const unlinkRecordingLocal = (
  unlinkRecordingProps: UnlinkRecordingProps
) => ({
  type: 'UNLINK_RECORDING',
  payload: unlinkRecordingProps,
});

export const relinkFeedCard = (
  recordingId: string,
  feedcardId: string,
  time: number
) => ({
  type: 'RELINK_FEEDCARD',
  payload: { recordingId, feedcardId, time },
});

export const unlinkFeedCard = (
  recordingId: string,
  feedcardId: string,
  time: number
) => ({
  type: 'UNLINK_FEEDCARD',
  payload: { recordingId, feedcardId, time },
});

export const setRelinkingFeedcard = (relinking: string | null) => ({
  type: 'SET_RELINKING_FEEDCARD',
  payload: relinking,
});

export const updateGiideSettings = (giideData: SettingsData) => ({
  type: 'UPDATE_GIIDE_SETTINGS',
  payload: giideData,
});

export const setAudioLibrary = (records: UserAudioLibrary[]) => ({
  type: 'SET_AUDIO_LIBRARY',
  payload: records,
});

export const addToAudioLibrary = (record: UserAudioLibrary) => ({
  type: 'ADD_TO_AUDIO_LIBRARY',
  payload: record,
});

export const updateAudioLibrary = (record: UserAudioLibrary) => ({
  type: 'UPDATE_AUDIO_LIBRARY',
  payload: record,
});

export const deleteFromAudioLibrary = (id: string) => ({
  type: 'DELETE_FROM_AUDIO_LIBRARY',
  payload: id,
});

export const setFeedcardValidationFailed = (
  feedcardValidationFailed: boolean
) => ({
  payload: feedcardValidationFailed,
  type: 'SET_FEEDCARD_VALIDATION_FAILED',
});

export const selectAudioFromLibrary = (
  id: string,
  length: number,
  filePath: string,
  processingState: string
) => ({
  type: 'SELECT_AUDIO_FROM_LIBRARY',
  payload: { id, length, filePath, processingState },
});

export const setPublishActiveStep = (index: number) => ({
  payload: index,
  type: 'SET_PUBLISH_ACTIVE_STEP',
});

export const setAccountPage = (accountPage: string) => ({
  payload: accountPage,
  type: 'SET_ACCOUNT_PAGE',
});

export const setEditingChapterId = (editingChapterId: string | undefined) => ({
  payload: editingChapterId,
  type: 'SET_EDITING_CHAPTER_ID',
});

export const setAudioUploadProgress = (
  audioUploadProgress: number | undefined
) => ({
  payload: audioUploadProgress,
  type: 'SET_AUDIO_UPLOAD_PROGRESS',
});

export const setAudioUploadFileName = (
  audioUploadFileName: string | undefined
) => ({
  payload: audioUploadFileName,
  type: 'SET_AUDIO_UPLOAD_FILE_NAME',
});

export const setAudioUploadFileId = (
  audioUploadFileId: string | undefined
) => ({
  payload: audioUploadFileId,
  type: 'SET_AUDIO_UPLOAD_FILE_ID',
});

export const setLeftSideTabState = (tabState: 'notes' | 'feed') => ({
  payload: tabState,
  type: 'SET_LEFT_SIDE_TAB_STATE',
});

export const updateScript = (script: string, id: string) => ({
  payload: { script, id },
  type: 'UPDATE_SCRIPT',
});

export const setUserFetching = (userLoading: boolean) => ({
  payload: userLoading,
  type: 'SET_USER_FETCHING',
});

export const setUserLoggingIn = (userLoggingIn: boolean) => ({
  payload: userLoggingIn,
  type: 'SET_USER_LOGGING_IN',
});

export const setPreviewDetails = (previewDetails: PreviewDetails) => ({
  payload: previewDetails,
  type: 'SET_PREVIEW_DETAILS',
});

export const setUseTemplate = (useTemplate: boolean) => ({
  payload: useTemplate,
  type: 'SET_USE_TEMPLATE',
});

export const setOldGiideData = (oldGiideData: SettingsData) => ({
  payload: oldGiideData,
  type: 'SET_OLD_GIIDE_DATA',
});

export const setNewImgLoadedBlob = (newImgBlob: string | undefined) => ({
  payload: newImgBlob,
  type: 'SET_NEW_IMG_LOADED_BLOB',
});

export const setImageEditorState = (
  imageEditorState: ImageEditorState | undefined
) => ({
  payload: imageEditorState,
  type: 'SET_IMAGE_EDITOR_STATE',
});

export const setPublishFlowGiideBeforePreview = (
  publishFlowGiideBeforePreview: SettingsData
) => ({
  payload: publishFlowGiideBeforePreview,
  type: 'SET_PUBLISH_FLOW_GIIDE_BEFORE_PREVIEW',
});

export const clearEditorData = (withTmpGiideSettings: boolean = false) => ({
  payload: withTmpGiideSettings,
  type: 'CLEAR_EDITOR_DATA',
});

export const setImageEditorValidation = (
  imageEditorValidation: ImageEditorValidation
) => ({
  payload: imageEditorValidation,
  type: 'SET_IMAGE_EDITOR_VALIDATION_FAILED',
});

export const setActiveWordInTranscribe = (
  activeWord: TranscriptItemInput | undefined
) => ({
  payload: activeWord,
  type: 'SET_ACTIVE_WORD_IN_TRANSCRIBE',
});

export const setAddFeedcardModalOpened = (fcModalOpened: boolean) => ({
  payload: fcModalOpened,
  type: 'SET_ADD_FEEDCARD_MODAL_OPENED',
});

export const updateGiideScript = (script: string, id: string) => ({
  payload: { script, id },
  type: 'UPDATE_GIIDE_SCRIPT',
});

export const setImageMimeTypeLoaded = (type: string) => ({
  payload: type,
  type: 'SET_IMAGE_MIME_TYPE_LOADED',
});

export const setNewFeedcardSaving = (newFeedcardSaving: boolean) => ({
  payload: newFeedcardSaving,
  type: 'SET_NEW_FEEDCARD_SAVING',
});

export const setOwnRecordingStarted = (
  ownRecordingStarted: boolean | undefined
) => ({
  payload: ownRecordingStarted,
  type: 'SET_OWN_RECORDING_STARTED',
});

export const setGetStartedModalOpen = (
  getStartedModalOpen: false | 'sample' | 'upload'
) => ({
  payload: getStartedModalOpen,
  type: 'SET_GET_STARTED_MODAL_OPEN',
});

export const setUpgradeModalOpen = (upgradeModalOpen: boolean) => ({
  payload: upgradeModalOpen,
  type: 'SET_UPGRADE_MODAL_OPEN',
});

export const setNeedToRefetchSubData = (needToRefetchSubData: boolean) => ({
  payload: needToRefetchSubData,
  type: 'SET_NEED_TO_REFETCH_SUB_DATA',
});

export const setAnalyticsID = (giideId?: string) => ({
  payload: giideId,
  type: 'SET_ANALYTICS_ID',
});

export const setAnalyticsData = (analyticsData?: GiideAnalytics) => ({
  payload: analyticsData,
  type: 'SET_ANALYTICS_DATA',
});

export const setPaddleAccountDetails = (
  paddleAccountDetails: PaddleAccountDetails
) => ({
  payload: paddleAccountDetails,
  type: 'SET_PADDLE_ACCOUNT_DETAILS',
});

export const setPaddleTransactions = (
  paddleTransactions: PaddleTransactions
) => ({
  payload: paddleTransactions,
  type: 'SET_PADDLE_TRANSACTIONS',
});

export const setPaymentProcessing = (paymentProcessing: boolean) => ({
  payload: paymentProcessing,
  type: 'SET_PAYMENT_PROCESSING',
});

export const setTranscriptData = (transcriptData?: TranscriptItemInput[]) => ({
  payload: transcriptData,
  type: 'SET_TRANSCRIPT_DATA',
});

export const setAccountDisabled = (isDisabled: boolean) => ({
  payload: isDisabled,
  type: 'SET_ACCOUNT_DISABLED',
});

export const setPaymentState = (state: PaymentState) => ({
  payload: state,
  type: 'SET_PAYMENT_STATE',
});

export const setCreateGiideError = (state: CreateGiideErrorType | null) => ({
  payload: state,
  type: 'SET_CREATE_GIIDE_ERROR',
});

export const updateExportVideoState = (state: ExportVideoState) => ({
  payload: state,
  type: 'SET_EXPORT_VIDEO_STATE',
});

export const setImageEditorImage = (state: ImageEditorImage) => ({
  payload: state,
  type: 'SET_IMAGE_EDITOR_IMAGE',
});

export const clearImageEditorImage = () => ({
  type: 'CLEAR_IMAGE_EDITOR_IMAGE',
});

export const addImageSubscriptionId = (state: string) => ({
  payload: state,
  type: 'ADD_IMAGE_SUBSCRIPTION_ID',
});

export const removeImageSubscriptionId = (state: string) => ({
  payload: state,
  type: 'REMOVE_IMAGE_SUBSCRIPTION_ID',
});

export const setImageChanged = (state: boolean) => ({
  payload: state,
  type: 'SET_IMAGE_CHANGED',
});

export const updateAIConverstion = (
  giide: string,
  type: 'user' | 'ai',
  content: string
) => ({
  payload: {
    giide,
    type,
    content,
  },
  type: 'UPDATE_CONVERSATION_HISTORY',
});

export const setAIConversation = (
  giide: string,
  chatHistory: AiChatHistory[]
) => ({
  type: 'SET_AI_CONVERSATION_HISTORY',
  payload: {
    giideId: giide,
    chatHistory,
  },
});

export const setGiideContentModal = (modal: ModalContent) => ({
  payload: modal,
  type: 'SET_GIIDE_CONTENT_MODAL',
});

export const setGiidePasswordModal = (
  giideId: string | null,
  modalType?: PasswordModalVariants
) => ({
  payload: {
    giideId,
    modalType,
  },
  type: 'SET_GIIDE_PASSWORD_MODAL',
});

export const unlockGiide = (giideId: string) => ({
  payload: giideId,
  type: 'UNLOCK_GIIDE',
});

export const lockGiide = (giideId: string) => ({
  payload: giideId,
  type: 'LOCK_GIIDE',
});

export const setCopyGiideModal = (giideId: string | null) => ({
  payload: giideId,
  type: 'SET_COPY_GIIDE_MODAL',
});
